import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "partials/Footer";
import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb";
import Button from "components/Button";
import Input from "components/form/Input";
import Textarea from "components/form/Textarea";
import RichTextEditor from "components/form/RichTextEditor";

const BASE_URL = "https://api.kennapartners.com/api/v1"; // Base URL for all API calls

// ─── HTTP status → descriptive message ───────────────────────────────────────
const parseInsightError = async (response) => {
  let body;
  try { body = await response.clone().json(); } catch { body = null; }
  const backendMsg = body?.detail
    ? (Array.isArray(body.detail) ? null : String(body.detail))
    : (body?.message ? String(body.message) : null);

  switch (response.status) {
    case 400:
      return backendMsg || "The request was invalid. Please check your input and try again.";
    case 401:
      return backendMsg || "Your session has expired. Please log out and log in again to continue.";
    case 403:
      return backendMsg || "You do not have permission to perform this action. Contact your administrator if you believe this is a mistake.";
    case 404:
      return backendMsg || "The requested insight could not be found. It may have been deleted.";
    case 409:
      return backendMsg || "An insight with this title already exists. Please use a different title.";
    case 413:
      return "The file you selected is too large. Please upload a file under 2 MB.";
    case 415:
      return "This file type is not supported. Please check the accepted formats and try again.";
    case 422: {
      if (body?.detail && Array.isArray(body.detail)) {
        const msgs = body.detail.map((err) => {
          const field = (err.loc || []).filter(l => typeof l === "string" && l !== "body").join(".");
          let msg = err.msg || "Invalid value";
          msg = msg.replace(/^value_error\./i, "").replace(/_/g, " ");
          msg = msg.charAt(0).toUpperCase() + msg.slice(1);
          return field ? `${field.replace(/_/g, " ")}: ${msg}` : msg;
        });
        return `Validation failed — ${msgs.join(" · ")}`;
      }
      return backendMsg || "Some fields are invalid. Please review your input and try again.";
    }
    case 429:
      return "Too many requests. Please wait a moment before trying again.";
    case 500:
      return backendMsg || "An internal server error occurred. Our team has been notified. Please try again in a few minutes.";
    case 502:
      return "The server is temporarily unavailable (bad gateway). Please try again shortly.";
    case 503:
      return "The service is currently unavailable or undergoing maintenance. Please try again later.";
    case 504:
      return "The request timed out. Please check your connection and try again.";
    default:
      return backendMsg || `An unexpected error occurred (code ${response.status}). Please try again or contact support.`;
  }
};

// ─── Toast component ──────────────────────────────────────────────────────────
const InsightToast = ({ toasts, onDismiss }) => {
  if (!toasts.length) return null;
  return (
    <div style={{position:"fixed",top:"20px",left:"50%",transform:"translateX(-50%)",zIndex:99999,display:"flex",flexDirection:"column",gap:"10px",width:"calc(100% - 32px)",maxWidth:"540px"}}>
      {toasts.map((t) => (
        <div key={t.id} style={{
          display:"flex",alignItems:"flex-start",gap:"12px",
          backgroundColor: t.type === "success" ? "#16a34a" : t.type === "warning" ? "#d97706" : "#dc2626",
          color:"white",fontSize:"14px",fontWeight:"500",
          padding:"14px 16px",borderRadius:"10px",
          boxShadow:"0 8px 32px rgba(0,0,0,0.3)",
          animation:"insightToastIn 0.25s ease",
        }}>
          <span style={{fontSize:"18px",lineHeight:"1.2",flexShrink:0}}>
            {t.type === "success" ? "✓" : t.type === "warning" ? "⚠️" : "✕"}
          </span>
          <div style={{flex:1,lineHeight:"1.6"}}>{t.message}</div>
          <button onClick={() => onDismiss(t.id)} style={{marginLeft:"4px",background:"none",border:"none",color:"white",fontSize:"20px",lineHeight:"1",cursor:"pointer",flexShrink:0,fontWeight:"bold",padding:0}}>×</button>
        </div>
      ))}
      <style>{`@keyframes insightToastIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
};


const Insights = () => {
  const [insights, setInsights] = useState([]);
  const [filteredInsights, setFilteredInsights] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authors, setAuthors] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [pdfFile, setPdfFile] = useState(null); // <-- new state for PDF upload

  const isInitialLoadRef = useRef(true); // Track if this is initial load
  const isSearchingRef = useRef(false); // Track if we're currently searching

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [file, setFile] = useState(null);

  // ─── Toast state ──────────────────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const pushToast = (message, type = "error", duration = 7000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration) setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), duration);
  };
  const dismissToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));


  // Current insight being operated on
  const [currentInsight, setCurrentInsight] = useState(null);
  const [selectedInsightId, setSelectedInsightId] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    authors: [],
    practice_area: "",
    date: new Date().toISOString().split("T")[0],
    file_url: null,
pdf_version_url: null, 
  });

  // Temporary input for array fields
  const [tempArrayInput, setTempArrayInput] = useState({
    authors: "",
  });

  const navigate = useNavigate();

useEffect(() => {
  fetchInsights();
  fetchAuthors();
  fetchPracticeAreas();
}, [currentPage]); // Keep currentPage only

// Initialize filteredInsights ONLY on initial load
useEffect(() => {
  if (insights.length > 0 && isInitialLoadRef.current && !isSearchingRef.current) {
    setFilteredInsights(insights);
    isInitialLoadRef.current = false; // Mark that initial load is done
  }
}, [insights]);

// Search functionality
useEffect(() => {
  if (searchTerm.trim()) {
    isSearchingRef.current = true;
    const debounceTimer = setTimeout(() => {
      searchAllInsights(searchTerm.trim());
    }, 300);
    return () => clearTimeout(debounceTimer);
  } else {
    // Search cleared - reset to current page
    isSearchingRef.current = false;
    setFilteredInsights(insights);
  }
}, [searchTerm]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${BASE_URL}/insights/?page=${currentPage}&limit=${itemsPerPage}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      if (data && data.data && Array.isArray(data.data.insight)) {
        setInsights(data.data.insight);
        setFilteredInsights(data.data.insight); // Initialize filtered insights
        setTotalItems(data.data.total_insight || 0);
      } else {
        console.error(
          'Invalid data format. Expected an array of insights under "data.insight".'
        );
        setInsights([]);
        setFilteredInsights([]);
        setTotalItems(0);
      }
      setError("");
    } catch (error) {
      console.error("Error fetching insights:", error);
      pushToast("Failed to load insights. Please check your connection and try again.", "error");
      setInsights([]);
      setFilteredInsights([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthors = async () => {
    try {
      const token = localStorage.getItem("access_token");
      let allAuthors = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(
          `${BASE_URL}/profiles/?page=${page}&limit=50`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        const fetched = data.data?.profiles || [];

        allAuthors = [...allAuthors, ...fetched];
        hasMore = fetched.length === 50;
        page += 1;
      }

      // Sort alphabetically by full_name
      allAuthors.sort((a, b) => a.full_name.localeCompare(b.full_name));
      setAuthors(allAuthors);
    } catch (error) {
      console.error("Error fetching authors:", error);
      pushToast("Failed to load authors. The author list may be incomplete. Please refresh the page.", "warning");
    }
  };

  const fetchPracticeAreas = async () => {
    try {
      const token = localStorage.getItem("access_token");
      let allPracticeAreas = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(
          `${BASE_URL}/practice-areas/?page=${page}&limit=50`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        const fetched = data.data?.practice_areas || [];

        allPracticeAreas = [...allPracticeAreas, ...fetched];
        hasMore = fetched.length === 50;
        page += 1;
      }

      // Sort alphabetically by name
      allPracticeAreas.sort((a, b) => a.name.localeCompare(b.name));
      setPracticeAreas(allPracticeAreas);
    } catch (error) {
      console.error("Error fetching practice areas:", error);
      pushToast("Failed to load practice areas. The practice area list may be incomplete. Please refresh the page.", "warning");
    }
  };

const searchAllInsights = async (term) => {
  setLoading(true);
  try {
    const token = localStorage.getItem("access_token");
    const response = await fetch(
      `${BASE_URL}/insights/?page=1&limit=10000`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();
    const allInsights = data.data?.insight || [];

    const filtered = allInsights.filter((insight) => {
      const titleMatch = insight?.title?.toLowerCase().startsWith(term.toLowerCase());
      return titleMatch; // ONLY return titles that START WITH the search term
    });

    setFilteredInsights(filtered);
  } catch (error) {
    console.error("Error searching insights:", error);
    pushToast("Search failed. Please check your connection and try again.", "error");
    setFilteredInsights([]);
  } finally {
    setLoading(false);
  }
};
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleArrayInputChange = (e) => {
    const { name, value } = e.target;
    setTempArrayInput((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addArrayItem = (field) => {
    if (field === "authors") {
      const selectedId = tempArrayInput[field];
      if (!selectedId) return;

      const selectedAuthor = authors.find((author) => author.id === selectedId);
      if (!selectedAuthor) return;

      const alreadyExists = formData.authors.some(
        (author) =>
          (typeof author === "object" ? author.id : author) === selectedId
      );
      if (alreadyExists) {
        pushToast("This author has already been added to the insight. Please select a different author.", "warning");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [field]: [...prev[field], selectedAuthor],
      }));
    }

    setTempArrayInput((prev) => ({
      ...prev,
      [field]: "",
    }));
    setError("");
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      authors: [],
      practice_area: "",
      file_url: null,
    });
    setTempArrayInput({
      authors: "",
    });
    setError("");
  };

  const resetAllFormStates = () => {
    setCurrentInsight(null);
    setSelectedInsightId(null);
    setFormData({
      title: "",
      content: "",
      authors: [],
      practice_area: "",
      file_url: null,
    });
    setTempArrayInput({
      authors: "",
    });
    setPdfFile(null); // <-- reset pdfFile when clearing forms
    setFile(null);
    setError("");
  };

  const handleCreateInsight = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.title.trim()) {
      pushToast("Title is required. Please enter a title for this insight before submitting.", "error");
      setLoading(false);
      return;
    }
    if (!formData.content.trim()) {
      pushToast("Content is required. Please add some content for this insight before submitting.", "error");
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("access_token");
    if (!token) {
      pushToast("You are not authenticated. Please log out and log in again, then try again.", "error");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        title: formData.title,
        content: { html: formData.content, text: formData.content.replace(/<[^>]*>/g, "") },
        authors: formData.authors.map((a) => a.id),
        practice_area: formData.practice_area,
        date: formData.date,
      };

      const response = await fetch(`${BASE_URL}/insights/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const msg = await parseInsightError(response);
        pushToast(msg, "error");
        return;
      }

      const data = await response.json();
      const newInsight = data.data?.insight;
      if (!newInsight?.id) {
        pushToast("Insight was saved but the server returned an unexpected response. Please refresh the page to confirm.", "warning");
        setShowCreateModal(false);
        resetAllFormStates();
        await fetchInsights();
        return;
      }

      // Image upload
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const uploadRes = await fetch(`${BASE_URL}/insights/${newInsight.id}/upload`, {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!uploadRes.ok) {
          const msg = await parseInsightError(uploadRes);
          pushToast(`Insight created, but the image could not be uploaded: ${msg}`, "warning");
        }
      }

      // PDF upload
      if (pdfFile) {
        const pd = new FormData();
        pd.append("file", pdfFile);
        const pdfRes = await fetch(`${BASE_URL}/insights/${newInsight.id}/pdf/upload`, {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
          body: pd,
        });
        if (!pdfRes.ok) {
          const msg = await parseInsightError(pdfRes);
          pushToast(`Insight created, but the PDF could not be uploaded: ${msg}`, "warning");
        } else {
          const pdfData = await pdfRes.json();
          newInsight.pdf_url = pdfData.data?.insight?.pdf_url;
        }
      }

      setShowCreateModal(false);
      resetAllFormStates();
      setFile(null);
      setPdfFile(null);
      pushToast("Insight created successfully!", "success", 4000);
      await fetchInsights();
    } catch (err) {
      console.error("Error creating insight:", err);
      if (!navigator.onLine) {
        pushToast("You appear to be offline. Please check your internet connection and try again.", "error");
      } else {
        pushToast(err.message || "An unexpected error occurred while creating the insight. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

const handleEditInsight = async (e) => {
  e.preventDefault();
  setLoading(true);

  if (!currentInsight?.id) {
    pushToast("Could not identify which insight to update. Please close this modal and try again.", "error");
    setLoading(false);
    return;
  }
  if (!formData.title.trim()) {
    pushToast("Title is required. Please enter a title before saving.", "error");
    setLoading(false);
    return;
  }
  if (!formData.content.trim()) {
    pushToast("Content is required. Please add some content before saving.", "error");
    setLoading(false);
    return;
  }

  const token = localStorage.getItem("access_token");
  if (!token) {
    pushToast("You are not authenticated. Please log out and log in again, then try again.", "error");
    setLoading(false);
    return;
  }

  try {
    const payload = {
      title: formData.title,
      content: { html: formData.content, text: formData.content.replace(/<[^>]*>/g, "") },
      authors: formData.authors.map((a) => a.id),
      practice_area: formData.practice_area,
      date: formData.date,
    };

    const response = await fetch(`${BASE_URL}/insights/${currentInsight.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const msg = await parseInsightError(response);
      pushToast(msg, "error");
      return;
    }

    const data = await response.json();
    const updatedInsight = data.data?.insight;
    if (!updatedInsight) {
      pushToast("Insight updated but the server returned an unexpected response. Please refresh the page.", "warning");
      setShowEditModal(false);
      await fetchInsights();
      return;
    }

    // Image upload
    if (file) {
      const fd = new FormData();
      fd.append("file", file);
      const uploadRes = await fetch(`${BASE_URL}/insights/${updatedInsight.id}/upload`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!uploadRes.ok) {
        const msg = await parseInsightError(uploadRes);
        pushToast(`Insight saved, but the image could not be uploaded: ${msg}`, "warning");
      } else {
        const uploadData = await uploadRes.json();
        updatedInsight.file_url = uploadData.data?.insight?.file_url;
      }
    }

    // PDF upload
    if (pdfFile) {
      const pd = new FormData();
      pd.append("file", pdfFile);
      const pdfRes = await fetch(`${BASE_URL}/insights/${updatedInsight.id}/pdf/upload`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: pd,
      });
      if (!pdfRes.ok) {
        const msg = await parseInsightError(pdfRes);
        pushToast(`Insight saved, but the PDF could not be uploaded: ${msg}`, "warning");
      } else {
        const pdfData = await pdfRes.json();
        updatedInsight.pdf_version_url = pdfData.data?.insight?.pdf_version_url;
      }
    }

    setInsights((prev) => prev.map((i) => (i.id === updatedInsight.id ? updatedInsight : i)));
    setCurrentInsight(updatedInsight);
    setShowEditModal(false);
    setFile(null);
    setPdfFile(null);
    pushToast("Insight updated successfully!", "success", 4000);
  } catch (err) {
    console.error("Error updating insight:", err);
    if (!navigator.onLine) {
      pushToast("You appear to be offline. Please check your internet connection and try again.", "error");
    } else {
      pushToast(err.message || "An unexpected error occurred while updating the insight. Please try again.", "error");
    }
  } finally {
    setLoading(false);
  }
};


  const handleDeleteInsight = async () => {
    setLoading(true);
    const token = localStorage.getItem("access_token");
    if (!token) {
      pushToast("You are not authenticated. Please log out and log in again, then try again.", "error");
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${BASE_URL}/insights/${selectedInsightId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const msg = await parseInsightError(response);
        pushToast(msg, "error");
        setShowDeleteModal(false);
        return;
      }

      setInsights((prev) => prev.filter((i) => i.id !== selectedInsightId));
      setFilteredInsights((prev) => prev.filter((i) => i.id !== selectedInsightId));
      setShowDeleteModal(false);
      setSelectedInsightId(null);
      if (filteredInsights.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
      pushToast("Insight deleted successfully.", "success", 4000);
    } catch (err) {
      console.error("Error deleting insight:", err);
      if (!navigator.onLine) {
        pushToast("You appear to be offline. Please check your internet connection and try again.", "error");
      } else {
        pushToast(err.message || "Failed to delete the insight. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsInsight = async (id) => {
    setLoading(true);
    const token = localStorage.getItem("access_token");
    try {
      const response = await fetch(`${BASE_URL}/insights/${id}`, {
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });

      if (!response.ok) {
        const msg = await parseInsightError(response);
        pushToast(msg, "error");
        setCurrentInsight(null);
        return;
      }

      const data = await response.json();
      const insightData = data.data?.insight;
      if (!insightData) {
        pushToast("The insight details could not be loaded. The server returned an empty response. Please try again.", "error");
        setCurrentInsight(null);
        return;
      }
      setCurrentInsight(insightData);
    } catch (err) {
      console.error("Error fetching insight details:", err);
      if (!navigator.onLine) {
        pushToast("You appear to be offline. Please check your internet connection and try again.", "error");
      } else {
        pushToast(err.message || "Failed to load insight details. Please try again.", "error");
      }
      setCurrentInsight(null);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCurrentInsight(null);
    resetForm();
    setFile(null);
    setError("");
    setShowCreateModal(true);
  };

  const openEditModal = (insight) => {
    setCurrentInsight(insight);
    setFormData({
      title: insight.title || "",
      content:
        typeof insight.content === "string"
          ? insight.content
          : insight.content.html || "",
      authors: insight.authors || [],
      practice_area: insight.practice_area?.id || insight.practice_area || "",
      date: insight.date
        ? insight.date.split("T")[0]
        : new Date().toISOString().split("T")[0],
      file_url: insight.file_url || null,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (insightId) => {
    setSelectedInsightId(insightId);
    setShowDeleteModal(true);
  };

  const openDetailsModal = async (insightId) => {
    setLoading(true);
    setShowDetailsModal(true);
    try {
      await handleDetailsInsight(insightId);
    } catch (error) {
      console.error("Error loading insight details:", error);
      setError("Failed to load insight details");
    } finally {
      setLoading(false);
    }
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowDetailsModal(false);
    setCurrentInsight(null);
    setSelectedInsightId(null);
    resetForm();
    setFile(null);
    setError("");
  };

  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  const renderArrayItems = (items) => {
    if (!items || !Array.isArray(items)) return null;

    return items.map((item, index) => (
      <span
        key={index}
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
      >
        {typeof item === "object" ? item.full_name : item}
        <button
          type="button"
          onClick={() => removeArrayItem("authors", index)}
          className="ml-1 text-blue-600 hover:text-blue-900"
        >
          ×
        </button>
      </span>
    ));
  };

  // Pagination functions
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-between items-center mt-4">
        <div>
          <p className="text-sm text-gray-700">
            Showing{" "}
            <span className="font-medium">
              {(currentPage - 1) * itemsPerPage + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(currentPage * itemsPerPage, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> insights
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded-md"
          >
            Previous
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <Button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-1 border rounded-md ${
                currentPage === page ? "" : ""
              }`}
            >
              {page}
            </Button>
          ))}
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded-md"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const renderPdfUploadField = () => (
    <div className="form-group space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Upload Insight PDF
      </label>
      <input
        type="file"
        onChange={(e) => {
          const f = e.target.files[0];
          if (!f) return;
          if (f.type !== "application/pdf") {
            pushToast(`"${f.name}" is not a PDF file. Only PDF files are accepted for this field.`, "error");
            e.target.value = ""; return;
          }
          if (f.size > 2097152) {
            pushToast(`"${f.name}" is ${(f.size/1048576).toFixed(1)} MB — over the 2 MB limit. Please use a smaller PDF.`, "error");
            e.target.value = ""; return;
          }
          setPdfFile(f);
        }}
        className="block w-full text-sm text-gray-600 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        accept=".pdf"
      />
      <p className="text-xs text-gray-500 mt-1">Supported format: PDF only · Max 2 MB</p>
    </div>
  );

  return (
    <main className="workspace">
      {/* Global Toast Notifications */}
      <InsightToast toasts={toasts} onDismiss={dismissToast} />

      <Breadcrumb title="Insights">
        <BreadcrumbItem link="#no-link">Pages</BreadcrumbItem>
        <BreadcrumbItem>Insights</BreadcrumbItem>
      </Breadcrumb>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Insight Management</h1>
        <div className="flex gap-2">
          <button
            onClick={() => {
              resetAllFormStates();
              setShowCreateModal(true);
            }}
            className="btn-primary"
          >
            <span className="la la-plus"></span> Add New Insight
          </button>
          <button
            onClick={fetchInsights}
            className="btn-secondary"
            disabled={loading}
          >
            <span className="la la-refresh"></span> Refresh
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex gap-4">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search insights by title or content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          {loading && !insights.length ? (
            <div className="text-center py-8">
              <span className="la la-spinner la-spin text-2xl"></span>
              <p className="mt-2">Loading insights...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover w-full">
                  <thead>
                    <tr>
                      <th className="w-1/5">Title</th>
                      <th className="w-1/5">Date</th>
                      <th className="w-1/5">Authors</th>
                      <th className="w-1/5">Practice Area</th>
                      <th className="w-1/5">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(filteredInsights) &&
                    filteredInsights.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-8">
                          {insights.length === 0
                            ? "No insights found. Create your first insight!"
                            : "No insights match your search criteria."}
                        </td>
                      </tr>
                    ) : (
                      filteredInsights.map((insight) => (
                        <tr key={insight.id}>
                          <td className="w-1/5">
                            <div className="font-medium">{insight.title}</div>
                          </td>
                          <td className="w-1/5">
                            {insight.date
                              ? new Date(insight.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )
                              : "-"}
                          </td>
                          <td className="w-1/5">
                            {insight.authors && insight.authors.length > 0
                              ? insight.authors
                                  .map((author) => author.full_name)
                                  .join(", ")
                              : "No authors"}
                          </td>
                          <td className="w-1/5">
                            {insight.practice_area
                              ? typeof insight.practice_area === "object"
                                ? insight.practice_area.name
                                : practiceAreas.find(
                                    (pa) => pa.id === insight.practice_area
                                  )?.name || "N/A"
                              : "N/A"}
                          </td>
                          <td className="w-1/5">
                            <div className="flex gap-1">
                              <Button
                                onClick={() => openDetailsModal(insight.id)}
                                className="btn btn-sm btn-info"
                                title="View Details"
                              >
                                <span className="la la-eye"></span>
                              </Button>
                              <Button
                                onClick={() => openEditModal(insight)}
                                className="btn btn-sm btn-warning"
                                title="Edit Insight"
                              >
                                <span className="la la-edit"></span>
                              </Button>
                              <Button
                                color="danger"
                                onClick={() => openDeleteModal(insight.id)}
                                className="btn btn-sm btn-danger"
                                title="Delete Insight"
                              >
                                <span className="la la-trash"></span>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Create New Insight
                  </h5>
                  <button
                    onClick={() => {
                      resetAllFormStates();
                      setShowCreateModal(false);
                    }}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <form onSubmit={handleCreateInsight}>
                  <div className="modal-body p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        Basic Information
                      </h3>

                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Title *
                        </label>
                        <Input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Content *
                        </label>
                        <RichTextEditor
                          value={formData.content}
                          onChange={(html) =>
                            setFormData((prev) => ({ ...prev, content: html }))
                          }
                          uploadEndpoint={`${BASE_URL}/insights/editor/upload`}
                        />
                      </div>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Authors
                      </label>
                      <div className="flex gap-2">
                        <select
                          name="authors"
                          value={tempArrayInput.authors || ""}
                          onChange={handleArrayInputChange}
                          className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select an author</option>
                          {authors.map((author) => (
                            <option key={author.id} value={author.id}>
                              {author.full_name}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          onClick={() => addArrayItem("authors")}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Add
                        </Button>
                      </div>
                      <div className="mt-2">
                        {renderArrayItems(formData.authors)}
                      </div>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Practice Area
                      </label>
                      <select
                        name="practice_area"
                        value={formData.practice_area}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select a practice area</option>
                        {practiceAreas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Date *
                      </label>
                      <Input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Insight Picture
                      </label>
                      <input
                        type="file"
                        onChange={(e) => {
                          const f = e.target.files[0];
                          if (!f) return;
                          const allowed = ["image/png","image/jpeg","image/jpg"];
                          if (!allowed.includes(f.type)) {
                            pushToast(`"${f.name}" is not a supported image type. Please upload a PNG or JPEG file.`, "error");
                            e.target.value = ""; return;
                          }
                          if (f.size > 2097152) {
                            pushToast(`"${f.name}" is ${(f.size/1048576).toFixed(1)} MB — over the 2 MB limit. Please choose a smaller image.`, "error");
                            e.target.value = ""; return;
                          }
                          setFile(f);
                        }}
                        className="block w-full text-sm text-gray-600 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        accept=".png,.jpeg,.jpg"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Supported formats: PNG, JPEG, JPG · Max 2 MB
                      </p>
                    </div>

                    {renderPdfUploadField()}
                  </div>

                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Creating...
                        </span>
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Edit Insight
                  </h5>
                  <button
                    onClick={() => {
                      resetAllFormStates();
                      setShowEditModal(false);
                    }}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <form onSubmit={handleEditInsight}>
                  <div className="modal-body p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        Basic Information
                      </h3>

                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Title *
                        </label>
                        <Input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Content *
                        </label>
                        <RichTextEditor
                          value={formData.content}
                          onChange={(html) =>
                            setFormData((prev) => ({ ...prev, content: html }))
                          }
                          uploadEndpoint={`${BASE_URL}/insights/editor/upload`}
                        />
                      </div>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Authors
                      </label>
                      <div className="flex gap-2">
                        <select
                          name="authors"
                          value={tempArrayInput.authors || ""}
                          onChange={handleArrayInputChange}
                          className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select an author</option>
                          {authors.map((author) => (
                            <option key={author.id} value={author.id}>
                              {author.full_name}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          onClick={() => addArrayItem("authors")}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Add
                        </Button>
                      </div>
                      <div className="mt-2">
                        {renderArrayItems(formData.authors)}
                      </div>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Practice Area
                      </label>
                      <select
                        name="practice_area"
                        value={formData.practice_area}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select a practice area</option>
                        {practiceAreas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Date *
                      </label>
                      <Input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Insight Picture
                      </label>
                      <input
                        type="file"
                        onChange={(e) => {
                          const f = e.target.files[0];
                          if (!f) return;
                          const allowed = ["image/png","image/jpeg","image/jpg"];
                          if (!allowed.includes(f.type)) {
                            pushToast(`"${f.name}" is not a supported image type. Please upload a PNG or JPEG file.`, "error");
                            e.target.value = ""; return;
                          }
                          if (f.size > 2097152) {
                            pushToast(`"${f.name}" is ${(f.size/1048576).toFixed(1)} MB — over the 2 MB limit. Please choose a smaller image.`, "error");
                            e.target.value = ""; return;
                          }
                          setFile(f);
                        }}
                        className="block w-full text-sm text-gray-600 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        accept=".png,.jpeg,.jpg"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {file ? `Selected: ${file.name}` : currentInsight?.file_url ? `Current: ${currentInsight.file_url.split("/").pop()}` : "No file currently attached"}
                      </p>
                      <p className="text-xs text-gray-500">Supported formats: PNG, JPEG, JPG · Max 2 MB</p>
                    </div>

                    {renderPdfUploadField()}
                  </div>

                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Updating...
                        </span>
                      ) : (
                        "Update"
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog relative w-auto my-6 mx-auto max-w-lg">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Confirm Delete
                  </h5>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <div className="modal-body p-6">
                  <p className="text-gray-700">
                    Are you sure you want to delete this insight? This action
                    cannot be undone.
                  </p>
                </div>

                <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                  <Button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteInsight}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Deleting...
                      </span>
                    ) : (
                      "Delete"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowDetailsModal(false)}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Insight Details
                  </h5>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <div className="modal-body p-6">
                  {loading ? (
                    <div className="text-center py-8">
                      <span className="la la-spinner la-spin text-2xl"></span>
                      <p className="mt-2">Loading insight details...</p>
                    </div>
                  ) : currentInsight ? (
                    <div className="space-y-6">
                      {/* Title */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Title
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-900">
                            {currentInsight.title || "N/A"}
                          </p>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Content
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <div
                            className="prose max-w-none overflow-y-auto max-h-[50vh]"
                            dangerouslySetInnerHTML={{
                              __html:
                                currentInsight.content?.html ||
                                currentInsight.content ||
                                "N/A",
                            }}
                          />
                        </div>
                      </div>

                      {/* Authors */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Authors
                          </h3>
                        </div>
                        <div className="col-span-2">
                          {currentInsight.authors?.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {currentInsight.authors.map((author, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {author.full_name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500">No authors assigned</p>
                          )}
                        </div>
                      </div>

                      {/* Practice Area */}
                      {currentInsight.practice_area && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="col-span-1">
                            <h3 className="text-sm font-medium text-gray-500">
                              Practice Area
                            </h3>
                          </div>
                          <div className="col-span-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {typeof currentInsight.practice_area === "object"
                                ? currentInsight.practice_area.name
                                : practiceAreas.find(
                                    (a) => a.id === currentInsight.practice_area
                                  )?.name || "N/A"}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Image Attachment */}
                      {currentInsight.file_url && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="col-span-1">
                            <h3 className="text-sm font-medium text-gray-500">
                              Image
                            </h3>
                          </div>
                          <div className="col-span-2">
                            <a
                              href={currentInsight.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-600 hover:text-blue-800"
                            >
                              <span className="la la-image mr-2"></span>
                              {currentInsight.file_url.split("/").pop()}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* PDF Attachment - NEW */}
            {currentInsight.pdf_version_url && (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <div className="col-span-1">
      <h3 className="text-sm font-medium text-gray-500">Insight PDF</h3>
    </div>
    <div className="col-span-2">
      <a
        href={currentInsight.pdf_version_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center text-red-600 hover:text-red-800"
      >
        <span className="la la-file-pdf mr-2"></span>
        {currentInsight.pdf_version_url.split("/").pop()}
      </a>
    </div>
  </div>
)}


                      {/* Created At */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Created
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-900">
                            {new Date(
                              currentInsight.created_at
                            ).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Updated At */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="col-span-1">
                          <h3 className="text-sm font-medium text-gray-500">
                            Last Updated
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-900">
                            {new Date(
                              currentInsight.updated_at
                            ).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-700">
                        Failed to load insight details
                      </p>
                    </div>
                  )}
                </div>

                <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                  <Button
                    onClick={() => setShowDetailsModal(false)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default Insights;